import React from "react";
import { MapPin, MonitorPlay, Check, Users, Infinity as InfinityIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";

const META = {
  inPerson: {
    title: "Attend in person",
    subtitle: "Be there live at the venue",
    Icon: MapPin,
  },
  online: {
    title: "Watch online",
    subtitle: "Stream from anywhere",
    Icon: MonitorPlay,
  },
};

export function TicketCard({ type, ticket, selected, onSelect, compact = false }) {
  const meta = META[type];
  const soldOut = ticket.soldOut || ticket.available === 0;
  const pctLeft =
    ticket.total && !ticket.unlimited
      ? Math.round((ticket.available / ticket.total) * 100)
      : null;

  return (
    <button
      type="button"
      data-testid={`ticket-card-${type}`}
      disabled={soldOut}
      onClick={() => !soldOut && onSelect?.(type)}
      className={cn(
        "group relative flex w-full flex-col rounded-2xl border bg-card p-6 text-left transition-all duration-200",
        selected
          ? "border-brand ring-2 ring-brand/30 shadow-md"
          : "border-border hover:border-brand/40 hover:shadow-sm",
        soldOut && "cursor-not-allowed opacity-60"
      )}
    >
      <div className="flex items-start justify-between">
        <span
          className={cn(
            "flex h-11 w-11 items-center justify-center rounded-xl",
            type === "inPerson" ? "bg-brand/10 text-brand" : "bg-purple-100 text-purple-600"
          )}
        >
          <meta.Icon className="h-5 w-5" />
        </span>
        {selected && !soldOut && (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-white">
            <Check className="h-3.5 w-3.5" />
          </span>
        )}
      </div>

      <h4 className="mt-4 font-heading text-lg font-bold tracking-tight text-foreground">
        {meta.title}
      </h4>
      <p className="text-sm text-muted-foreground">{meta.subtitle}</p>

      <div className="mt-4 flex items-baseline gap-1">
        {soldOut ? (
          <span className="font-heading text-xl font-extrabold text-slate-500">Sold out</span>
        ) : (
          <>
            <span className="font-heading text-3xl font-extrabold tracking-tight text-foreground">
              {ticket.currency}
              {ticket.price}
            </span>
            <span className="text-sm text-muted-foreground">/ ticket</span>
          </>
        )}
      </div>

      {/* Availability */}
      <div className="mt-3 min-h-[34px]">
        {ticket.unlimited ? (
          <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
            <InfinityIcon className="h-4 w-4" /> Unlimited access
          </span>
        ) : soldOut ? (
          <span className="text-sm font-medium text-slate-500">No seats left</span>
        ) : (
          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                {ticket.available} of {ticket.total} left
              </span>
              <span>{pctLeft}%</span>
            </div>
            <Progress value={pctLeft} className="mt-1.5 h-1.5" />
          </div>
        )}
      </div>

      {!compact && (
        <ul className="mt-5 space-y-2 border-t border-border pt-5">
          {ticket.perks.map((perk) => (
            <li key={perk} className="flex items-center gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 shrink-0 text-brand" />
              {perk}
            </li>
          ))}
        </ul>
      )}

      <span
        className={cn(
          "mt-6 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold transition-colors",
          soldOut
            ? "bg-secondary text-slate-400"
            : selected
            ? "bg-brand text-white"
            : "bg-secondary text-foreground group-hover:bg-brand group-hover:text-white"
        )}
      >
        {soldOut ? "Sold out" : selected ? "Selected" : "Choose this ticket"}
      </span>
    </button>
  );
}

export default TicketCard;
